import React, { useState } from "react";
import {
  Formik,
  Form,
  Field,
  ErrorMessage,
  useField,
  useFormikContext,
} from "formik";
import * as Yup from "yup";
import {
  TypeUser,
  UtilisateurDto,
  defaultUtilisateur,
} from "@/types/UtilisateurType";
import { inscrireUtilisateur } from "@/pages/api/authentificationApi";
import styles from "./Formulaire.module.css";
import { FormulaireInscriptionProps } from "@/types/FormulaireInscriptionType";
import { InputFieldProps } from "@/types/InputFieldProps ";
import InputAutocomplete from "../inputAutocomplete/InputAutocompleteComponent";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  PUBLIC_TYPE_USERS,
  COMMON_TYPE_USERS,
} from "@/config/configFormulaire";
import { useNavigate } from "react-router-dom";
const PHONE_PREFIX = import.meta.env.VITE_PHONE_PREFIX || "+221";
const PHONE_LENGTH = Number(import.meta.env.VITE_PHONE_LENGTH) || 9;
import { UiAdvancedSelect, UiButton } from "../ui";
import { z } from "zod";

// Calcul des bornes de dates
const today = new Date();

const minBirth = new Date(today);
minBirth.setFullYear(today.getFullYear() - 100);

const maxBirth = new Date(today);
maxBirth.setFullYear(today.getFullYear() - 10);

const formatDateToFrench = (date: string | Date) => {
  if (!date) return "";
  return format(new Date(date), "dd/MM/yyyy", { locale: fr });
};

// Validation du schéma
const validationSchema = (villes: string[]) =>
  Yup.object().shape({
    type: Yup.string().required("Le type de profil est obligatoire"),
    nom: Yup.string().when("type", {
      is: (val: string) => val === TypeUser.ENTREPRISE,
      then: (schema) =>
        schema
          .required("Le nom de l'entreprise est obligatoire")
          .min(2, "Le nom de l'entreprise doit contenir au moins 2 caractères")
          .max(50, "Le nom de l'entreprise ne peut pas dépasser 50 caractères"),
      otherwise: (schema) =>
        schema
          .required("Le nom est obligatoire")
          .min(2, "Le nom doit contenir au moins 2 caractères")
          .max(50, "Le nom ne peut pas dépasser 50 caractères"),
    }),
    prenom: Yup.string()
      .when("type", {
        is: TypeUser.PARTICULIER,
        then: (schema) =>
          schema
            .required("Le prénom est obligatoire")
            .min(2, "Le prénom doit contenir au moins 2 caractères")
            .max(50, "Le prénom ne peut pas dépasser 50 caractères"),
      })
      .when("type", {
        is: TypeUser.ENTREPRISE,
        then: (schema) =>
          schema
            .required("Le NINEA est obligatoire")
            .min(2, "Le NINEA doit contenir au moins 2 caractères")
            .max(15, "Le NINEA ne peut pas dépasser 15 caractères"),
      }),
    email: Yup.string()
      .required("L'email est obligatoire")
      .email("L'email n'est pas valide"),

    phone: Yup.string()
      .required("Le téléphone est obligatoire")
      .matches(
        /^\d{9}$/,
        `Le numéro doit contenir exactement ${PHONE_LENGTH} chiffres`
      ),

    dateNaissance: Yup.date().when("type", {
      is: (val: string) => val === TypeUser.PARTICULIER,
      then: (schema) =>
        schema
          .required("La date de naissance est obligatoire")
          .min(
            minBirth,
            `La date de naissance doit être après le ${minBirth.toLocaleDateString(
              "fr-FR"
            )}`
          )
          .max(
            maxBirth,
            `La date de naissance doit être avant le ${maxBirth.toLocaleDateString(
              "fr-FR"
            )}`
          ),
    }),
    adresse: Yup.object()
      .shape({
        pays: Yup.string(),
        ville: Yup.string(),
        intituleRue: Yup.string().required("L'adresse est obligatoire"),
        numeroRue: Yup.number().nullable(),
      })
      .when("type", {
        is: (val: string) => val === TypeUser.ENTREPRISE,
        then: (schema) =>
          schema.shape({
            pays: Yup.string().required("Le pays est obligatoire"),
            ville: Yup.string().required("La ville est obligatoire"),
            numeroRue: Yup.number()
              .nullable()
              .required("Le numéro de rue est obligatoire"),
          }),
        otherwise: (schema) =>
          schema.shape({
            intituleRue: Yup.string().required("L'adresse est obligatoire"),
          }),
      }),
    villeNaissance: Yup.string()
      .required("La ville de naissance est obligatoire")
      .test("is-valid-city", "Ville non valide", (value) =>
        villes.includes(value || "")
      ),
    villeActivite: Yup.string()
      .required("La ville d'activité est obligatoire")
      .test("is-valid-city", "Ville non valide", (value) =>
        villes.includes(value || "")
      ),
  });

const InputField: React.FC<InputFieldProps> = ({
  name,
  type = "text",
  placeholder,
  disabled = false,
}) => {
  const [field, meta] = useField(name);
  const { setFieldValue } = useFormikContext();

  const formatDateForInput = (dateValue: any) => {
    if (type !== "date" || !dateValue) return "";

    if (
      typeof dateValue === "string" &&
      dateValue.match(/^\d{4}-\d{2}-\d{2}$/)
    ) {
      return dateValue;
    }

    try {
      const date = new Date(dateValue);
      return date.toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === "tel") {
      let value = e.target.value.replace(/\D/g, "");
      if (value.startsWith(PHONE_PREFIX.replace(/\D/g, ""))) {
        value = value.substring(PHONE_PREFIX.replace(/\D/g, "").length);
      }
      if (value.length > PHONE_LENGTH) value = value.slice(0, PHONE_LENGTH);
      setFieldValue(name, value);
    } else {
      field.onChange(e);
    }
  };

  const formatPhoneValue = (value: string) => {
    if (!value) return "";
    return value.replace(new RegExp(`^${PHONE_PREFIX.replace(/\D/g, "")}`), "");
  };

  return (
    <div className={styles.formGroup}>
      <input
        {...field}
        type={type}
        placeholder=" "
        disabled={disabled}
        value={
          type === "date"
            ? formatDateForInput(field.value)
            : type === "tel"
            ? formatPhoneValue(field.value || "")
            : field.value || ""
        }
        onChange={type === "tel" ? handleChange : field.onChange}
        className={`${styles.input} ${
          meta.touched && meta.error ? styles.error : ""
        }`}
      />
      <label className={styles.formLabel} htmlFor={name}>
        {placeholder}
      </label>
      {meta.touched && meta.error && (
        <p className={styles.errorMessage}>{meta.error}</p>
      )}
    </div>
  );
};

const FormulaireInscription: React.FC<FormulaireInscriptionProps> = ({
  isInscription,
  typeUser = TypeUser.PARTICULIER,
  isDisplayConditions,
  initialValues = defaultUtilisateur,
  villes,
  configFormulaire,
  onTypeChange,
  onSubmit,
  availableTypes,
  isEditing = false,
}) => {
  const navigate = useNavigate();

  const defaultValues: UtilisateurDto = {
    ...defaultUtilisateur,
    type: TypeUser.PARTICULIER,
    ...initialValues,
    villeNaissance: initialValues.villeNaissance || "",
    villeActivite: initialValues.villeActivite || "",
    adresse: {
      ...defaultUtilisateur.adresse,
      ...(initialValues.adresse || {}),
    },
  };
  const [villeNaissance, setVilleNaissance] = useState(
    defaultValues.villeNaissance || ""
  );
  const [villeActivite, setVilleActivite] = useState(
    defaultValues.villeActivite || ""
  );
  const [submitting, setSubmitting] = useState(false);

  const [acceptConditions, setAcceptConditions] = useState(false);
  const [status, setStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [forceDisabled, setForceDisabled] = useState(false);
  const displayTypes =
    availableTypes || (isInscription ? PUBLIC_TYPE_USERS : COMMON_TYPE_USERS);

  const handleSubmit = async (values: any, options?: any) => {
    try {
      setSubmitting(true);
      let formattedValues: any = {};
      if (values.type === TypeUser.PARTICULIER) {
        formattedValues = {
          nom: values.nom.toUpperCase(),
          prenom: values.prenom,
          phone: `${PHONE_PREFIX} ${values.phone}`,
          email: values.email,
          dateNaissance: formatDateToFrench(values.dateNaissance),
          adresse: {
            pays: values.adresse.pays || "Sénégal",
            cp: values.adresse.cp || "115",
            ville: values.adresse.ville || "DAKAR",
            intituleRue: values.adresse.intituleRue,
            numeroRue: values.adresse.numeroRue || 0,
          },
          villeNaissance: villeNaissance.replace(/\s+/g, ""),
          villeActivite: villeActivite.replace(/\s+/g, ""),
          type: values.type,
        };
      } else if (values.type === TypeUser.ENTREPRISE) {
        formattedValues = {
          nom: values.nom.toUpperCase(),
          prenom: values.prenom,
          phone: `${PHONE_PREFIX} ${values.phone}`,
          email: values.email,
          dateNaissance: values.dateNaissance
            ? formatDateToFrench(values.dateNaissance)
            : "",
          adresse: {
            pays: values.adresse.pays || "Sénégal",
            cp: values.adresse.cp || "115",
            ville: values.adresse.ville || "DAKAR",
            intituleRue: values.adresse.intituleRue,
            numeroRue: values.adresse.numeroRue || 0,
          },
          villeNaissance: villeNaissance.replace(/\s+/g, ""),
          villeActivite: villeActivite.replace(/\s+/g, ""),
          type: values.type,
        };
      } else {
        formattedValues = {
          nom: values.nom.toUpperCase(),
          prenom: values.prenom,
          phone: `${PHONE_PREFIX} ${values.phone}`,
          email: values.email,
          dateNaissance: values.dateNaissance
            ? formatDateToFrench(values.dateNaissance)
            : "",
          adresse: {
            pays: values.adresse.pays || "Sénégal",
            cp: values.adresse.cp || "115",
            ville: values.adresse.ville || "DAKAR",
            intituleRue: values.adresse.intituleRue,
            numeroRue: values.adresse.numeroRue || 0,
          },
          villeNaissance: villeNaissance,
          villeActivite: villeActivite,
          type: values.type,
        };
      }

      let result;

      if (onSubmit) {
        result = await onSubmit(formattedValues);
      } else {
        await inscrireUtilisateur(formattedValues);
        result = {
          success: true,
          message: "Inscription réussie",
          redirectUrl: "/verification-code",
        };

        localStorage.setItem("pendingEmail", values.email);
        localStorage.setItem("authContext", "signup");
      }

      setStatus({
        success: result.success,
        message: result.message,
      });

      if (result.success) {
        // resetForm();
        setAcceptConditions(false);
        setSubmitting(false);

        if (result.redirectUrl) {
          navigate(result.redirectUrl);
        }
      }
    } catch (error) {
      setStatus({
        success: false,
        message:
          typeof error === "string" ? error : "Erreur lors de l'opération",
      });
    } finally {
      // setSubmitting(false);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{
        ...defaultUtilisateur,
        ...initialValues,
        dateNaissance: initialValues.dateNaissance
          ? new Date(initialValues.dateNaissance).toISOString().split("T")[0]
          : "",
        villeNaissance: initialValues.villeNaissance || "",
        villeActivite: initialValues.villeActivite || "",
        type: initialValues.type || TypeUser.PARTICULIER,
      }}
      enableReinitialize={true}
      validationSchema={validationSchema(villes)}
      context={{ villes }}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, isSubmitting, dirty }) => {
        React.useEffect(() => {
          if (onTypeChange && values.type) {
            onTypeChange(values.type as TypeUser);
          }
        }, [values.type, onTypeChange]);

        React.useEffect(() => {}, [initialValues]);

        React.useEffect(() => {
          if (isEditing) {
            setForceDisabled(false);
          }
        }, [isEditing, initialValues]);
        return (
          <Form className="w-full">
            {isInscription && (
              <h2 className={styles.formTitle}>
                Qui êtes-vous ? Sélectionnez votre profil
              </h2>
            )}

            <div className={styles.formGroup}>
              <Field
                as="select"
                name="type"
                className={`${styles.select} ${
                  errors.type && touched.type ? styles.error : ""
                }`}
              >
                {displayTypes.map((userType) => (
                  <option key={userType} value={userType}>
                    {userType}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="type"
                component="p"
                className={styles.errorMessage}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {values.type === TypeUser.PARTICULIER && (
                <>
                  <InputField name="prenom" placeholder="Prénom" />
                  <InputField name="nom" placeholder="Nom" />
                  <InputField
                    name="dateNaissance"
                    type="date"
                    placeholder="Date de naissance"
                  />
                  {/* Pour le particulier, on n'affiche qu'un seul champ d'adresse */}
                  <InputField
                    name="adresse.intituleRue"
                    placeholder="Adresse"
                  />
                </>
              )}

              {values.type === TypeUser.ENTREPRISE && (
                <>
                  <InputField name="nom" placeholder="Nom de l'entreprise" />
                  <InputField name="prenom" placeholder="NINEA" />
                  <InputField
                    name="dateNaissance"
                    type="date"
                    placeholder="Date de création"
                  />
                  {/* Pour l'entreprise, tous les champs d'adresse sont affichés */}
                  <InputField
                    name="adresse.pays"
                    placeholder="Pays"
                    disabled={true}
                  />
                  <InputField name="adresse.ville" placeholder="Ville" />
                  <InputField name="adresse.intituleRue" placeholder="Rue" />
                  <InputField
                    name="adresse.numeroRue"
                    type="number"
                    placeholder="Numéro de rue"
                  />
                </>
              )}

              {values.type &&
                values.type !== TypeUser.PARTICULIER &&
                values.type !== TypeUser.ENTREPRISE && (
                  <>
                    <InputField name="prenom" placeholder="Prénom" />
                    <InputField name="nom" placeholder="Nom" />
                    <InputField
                      name="dateNaissance"
                      type="date"
                      placeholder="Date de Naissance"
                    />

                    <InputField
                      name="adresse.intituleRue"
                      placeholder="Adresse"
                    />
                  </>
                )}

              <InputField
                name="phone"
                type="tel"
                placeholder="Numéro de téléphone"
              />
              <InputField name="email" type="email" placeholder="Email" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <UiAdvancedSelect
                className="w-full relative"
                label="Ville de naissance"
                name="villeNaissance"
                loading={false}
                defaultValue={villeNaissance}
                placeholder="Choisissez une option"
                styles={{ zIndex: 9999 }}
                onChange={(value) => {
                  setVilleNaissance(value || "");
                }}
                options={villes.map((ville) => ({
                  value: ville,
                  label: ville,
                }))}
              ></UiAdvancedSelect>

              <UiAdvancedSelect
                className="w-full"
                label="Ville d'activité"
                name="villeActivite"
                loading={false}
                defaultValue={villeActivite}
                styles={{ zIndex: 9999 }}
                placeholder="Choisissez une option"
                onChange={(value) => {
                  setVilleActivite(value || "");
                }}
                options={villes.map((ville) => ({
                  value: ville,
                  label: ville,
                }))}
              ></UiAdvancedSelect>
            </div>

            {isDisplayConditions && (
              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={acceptConditions}
                    onChange={(e) => setAcceptConditions(e.target.checked)}
                    className={styles.checkbox}
                  />
                  <span>
                    J'accepte les{" "}
                    <a href="#" className={styles.link}>
                      conditions générales d'utilisation
                    </a>
                  </span>
                </label>
              </div>
            )}

            <div className="flex justify-center">
              <UiButton
                type="submit"
                disabled={
                  forceDisabled ||
                  submitting ||
                  (!isEditing && !dirty) ||
                  (isDisplayConditions && !acceptConditions)
                }
                loading={submitting}
                onClick={() => handleSubmit(values)}
              >
                {submitting
                  ? "Envoi en cours..."
                  : isInscription
                  ? "Je finalise mon inscription"
                  : isEditing
                  ? "Modifier l'utilisateur"
                  : "Ajouter l'utilisateur"}
              </UiButton>
            </div>

            {status && (
              <p
                className={`${styles.message} ${
                  status.success ? styles.messageSuccess : styles.messageError
                }`}
              >
                {status.message}
              </p>
            )}

            {isInscription && (
              <div className={styles.loginContainer}>
                <span>
                  Déjà inscrit ?{" "}
                  <a href="/connexion" className={styles.login}>
                    Connectez-vous ici
                  </a>
                </span>
              </div>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default FormulaireInscription;
